import { format } from "date-fns";

export function formatUTCDate(timestamp: number): string {
  const d = new Date(timestamp);
  return format(
    new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
    "dd/MM/yyyy",
  );
}

// Canonical copy lives with the map layer (InfoBalloon renders it). Deep
// path on purpose — the map barrel would drag react-map-gl into every module
// importing these utils.
export { formatAddressShort } from "@/features/vision-360/map/utils/address";

import type { RankedSpace } from "../ai-mode/types";

/**
 * Returns a display name for a space in the proposal context.
 * Parses neighborhood from Brazilian address format ("Rua X, 123 - Bairro, Cidade/UF").
 * Returns "Opção N - Bairro, Cidade" if parseable, else "Opção N - Cidade".
 */
/**
 * Calculates the Haversine distance between two coordinates in kilometers.
 */
export function haversineDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number,
): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Returns true if ALL spaces match the searched location (city + neighborhood).
 * Tokenizes targetCity into meaningful words and checks each against space city + address.
 */
export function allSpacesMatchLocation(
  spaces: RankedSpace[],
  targetCity: string | undefined,
): boolean {
  if (!targetCity || spaces.length === 0) return false;
  const targetWords = targetCity
    .toLowerCase()
    .split(/[\s,\-/]+/)
    .filter((w) => w.length > 2);
  if (targetWords.length === 0) return false;
  return spaces.every((s) => {
    const haystack = `${s.city || ""} ${s.address || ""}`.toLowerCase();
    return targetWords.every((w) => haystack.includes(w));
  });
}

export function getSpaceDisplayName(
  space: RankedSpace,
  index: number,
  optionLabel?: string,
): string {
  const label = optionLabel ?? `Opção ${index + 1}`;
  if (space.address) {
    const parts = space.address.split(" - ");
    if (parts.length > 1) {
      const neighborhood = parts[1].split(",")[0].trim();
      if (neighborhood) {
        return `${label} - ${neighborhood}, ${space.city}`;
      }
    }
  }
  return `${label} - ${space.city}`;
}
